/**
 * What-If RESULT canvas state (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md §4 RESULT row, §11.2).
 *
 * `← Examples & recents` is NOT rendered here — WhatIfView already
 * renders that affordance above the canvas region for every non-home
 * state (including 'result'), so this component starts directly with
 * the headline (same composition rule ProposalState uses: input/output
 * names + scenario name come from the form's curated-option lookups,
 * not the backend's headline parts) and the controls row.
 *
 * Provenance time (task brief: "NEVER render-time now()"): `calculatedAt`
 * comes from the run record's own `result.calculated_at` — WhatIfView
 * passes it straight through from whatever `getWhatIfRun` returned,
 * whether this is a live approve→poll completion or a reopened old run.
 * This is the one field that must never be replaced with `new Date()`.
 *
 * Output chart: the analyst ES two-series treatment
 * (EditScenariosChartResult — PublishedData solid "current" vs
 * PreviewData dashed "what-if", showScenarios={false}), same component
 * ProposalState's sibling input-delta chart doesn't use (that one is a
 * different, input-side payload) — this is the OUTPUT chart_payload
 * from the completed run. EditScenariosChartResult has no error
 * boundary of its own; ChartVizResult's IS reused indirectly here by
 * guarding on chart_payload shape before rendering (same emptiness
 * check ChartVizResult performs internally) and rendering the identical
 * calm "Chart data unavailable" fallback copy on a malformed payload,
 * rather than forking a second boundary implementation.
 *
 * Controls row ("View approved changes · ⬇ Download (Excel)",
 * with "Calculated <at>" on its right edge): rendered ABOVE the chart,
 * directly under the headline (ruled 2026-08-22 — below a tall
 * chart+table the links needed scrolling to discover; departs from the
 * design doc §4's "last line" placement). The former provenance
 * subheader is gone (same day's ruling): scenario name already lives in
 * the headline and "what-if vs current" is the chart legend's job — the
 * timestamp was its only net-new information.
 * "View approved changes" flips the canvas to WhatIfView's read-only
 * ProposalState render (§11.2) — this component only fires the
 * callback; WhatIfView owns the canvas-state transition itself, same
 * split ProposalState uses for Approve/Discard.
 */
import React, { useMemo, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { CloudDownload } from '@mui/icons-material';

import { EditScenariosChartResult } from 'src/components/AgenticV2/cards/results/EditScenariosChartResult';
import { OutputsTableRef } from 'src/components/AgenticV2/cards/results/charts/ESGenerateChartPanel';

import { clipChartPayloadToYearWindow, YearWindow } from '../execYearWindow';
import { formatUtcTimestamp } from '../formatUtcTimestamp';
import { WhatIfResult } from '../types';

interface ResultStateProps {
  result: WhatIfResult;
  // Same client-composed headline inputs ProposalState takes — see its
  // file header for why these aren't read off the backend's headline
  // parts.
  inputName: string;
  scenarioName: string;
  onViewApprovedChanges: () => void;
  // Exec AI year window (C1): clips the result chart's years — and, because the Original/Recalculated table and
  // its Excel export read the same payload, those too. Absent/null bounds = show all years.
  yearWindow?: YearWindow | null;
}

const formatCalculatedAt = (iso: string): string => {
  // formatUtcTimestamp, not bare new Date(): calculated_at is a
  // NAIVE-UTC ISO (the run's terminal_at via .isoformat(), no zone
  // suffix) — the old local-parse here displayed raw UTC ("Calculated
  // Aug 24, 3:58 AM" at 11:58 PM local, caught 2026-08-23).
  return formatUtcTimestamp(iso);
};

// Same emptiness rule ChartVizResult applies to the analyst chart
// payload (dates AND at least one series) — the What-If output
// chart_payload is the same forecast-API chart shape wrapped under
// `data`, just consumed here by EditScenariosChartResult instead.
interface ChartDataBlock {
  PreviewData?: unknown[];
  PublishedData?: unknown[];
  Dates?: unknown[];
}

const hasChartData = (payload: Record<string, unknown> | null | undefined) => {
  if (!payload) return false;
  const data = (payload as { data?: ChartDataBlock }).data;
  if (!data) return false;
  const previewLen = data.PreviewData?.length ?? 0;
  const publishedLen = data.PublishedData?.length ?? 0;
  const datesLen = data.Dates?.length ?? 0;
  return datesLen > 0 && (previewLen > 0 || publishedLen > 0);
};

const ResultState: React.FC<ResultStateProps> = ({
  result,
  inputName,
  scenarioName,
  onViewApprovedChanges,
  yearWindow,
}) => {
  const calculatedAt = result.calculated_at
    ? formatCalculatedAt(result.calculated_at)
    : '';
  // "Download (Excel)" here exports the RESULT — the Original vs
  // Recalculated table this screen shows — via the OutputsTable's own
  // ag-Grid exportDataAsExcel, reached through the tableExportRef chain
  // (ruled 2026-08-22: the old link served the CHANGES file on both
  // screens, which on RESULT the label misrepresents; the changes file
  // stays downloadable where the changes are shown — PROPOSAL and
  // "View approved changes").
  const tableExportRef = useRef<OutputsTableRef>(null);
  // Exec AI year window (C1): clip the payload ONCE here, before it reaches the shared analyst renderer. The chart,
  // the range slider, the Original/Recalculated table and its Excel export all read this one payload, so this is
  // the single clip site for the whole RESULT screen — EditScenariosChartResult itself stays analyst-agnostic.
  // Memoized so the renderer's one-time state init sees a stable object (the helper returns the same reference
  // when there is nothing to clip).
  const chartPayload = useMemo(
    () => clipChartPayloadToYearWindow(result.chart_payload, yearWindow),
    [result.chart_payload, yearWindow]
  );
  const chartAvailable = hasChartData(chartPayload);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* h5 for parity with ProposalState's headline (2026-08-22 mark 1);
          mb 0.5 + controls-row mb 1 mirror its 2026-08-23 tightening
          (screenshot -21). */}
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        If {inputName} changes — result for {scenarioName}.
      </Typography>

      {/* No provenance subheader (ruled 2026-08-22): the scenario name
          is already in the headline and "what-if vs current" is the
          chart legend's job — the calculated-at timestamp was the only
          net-new information, so it rides the controls row's right edge
          instead of costing its own line. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        {/* ml -5px = the text-button's own horizontal padding
            (sizeSmall '4px 5px'), so the LABEL text — not the invisible
            button box — aligns flush with the headline above (2026-08-22,
            screenshot -17: the headline read as outdented). Same
            treatment on ProposalState's row-first button. */}
        <Button
          variant="text"
          size="small"
          onClick={onViewApprovedChanges}
          sx={{ ml: '-5px' }}
        >
          View approved changes
        </Button>
        {chartAvailable && (
          <>
            <Typography variant="body2" color="text.secondary">
              ·
            </Typography>
            <Button
              variant="text"
              size="small"
              startIcon={<CloudDownload fontSize="small" />}
              onClick={() => tableExportRef.current?.exportToExcel()}
            >
              Download (Excel)
            </Button>
          </>
        )}
        {calculatedAt && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 'auto' }}
          >
            Calculated {calculatedAt}
          </Typography>
        )}
      </Box>

      {chartAvailable ? (
        <EditScenariosChartResult
          chartPayload={chartPayload}
          tableExportRef={tableExportRef}
          // Exec chart frame (2026-09-07 ruling): outlined frame around the chart block, its edges in line with
          // the Original/Recalculated table below and the control bar's content edges.
          frameChart
          // Flush with the headline — the analyst panel's default 24px
          // Container gutters made RESULT's content sit right of the
          // header while the changes screen's grid sits flush
          // (alignment ruling, 2026-08-22).
          disableGutters
        />
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Chart data unavailable.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ResultState;
